import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, User, Handshake as HandshakeIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export function OffersTab({ offers, offerStatusConfig, processingOfferId, handleAcceptOffer, handleDenyOffer, handleOpenCounterDialog }) {
  if (offers.length === 0) {
    return (
      <div className="text-center py-16 bg-surface rounded-2xl border border-[#262626]">
        <HandshakeIcon className="w-16 h-16 mx-auto mb-4 text-[#333]" />
        <h3 className="text-xl font-heading font-bold mb-2">No Offers Yet</h3>
        <p className="text-[#A1A1AA]">When buyers negotiate prices for bulk orders, they'll appear here</p>
      </div>
    );
  }

  const pendingOffers = offers.filter(o => o.status === 'pending');
  const historyOffers = offers.filter(o => o.status !== 'pending');

  return (
    <div className="space-y-4">
      {pendingOffers.length > 0 && (
        <div className="mb-6">
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />Pending Offers</h2>
          <div className="space-y-4">
            {pendingOffers.map((offer) => {
              const status = offerStatusConfig[offer.status];
              const StatusIcon = status.icon;
              const isProcessing = processingOfferId === offer.id;
              const discount = Math.round((1 - offer.proposed_price / offer.original_total) * 100);
              return (
                <motion.div key={offer.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-surface border border-yellow-500/30 rounded-xl p-6" data-testid={`offer-${offer.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-surface-highlight rounded-full flex items-center justify-center"><User className="w-5 h-5" /></div>
                      <div><h3 className="font-bold">{offer.user_name}</h3><p className="text-sm text-[#A1A1AA]">{offer.user_email}</p></div>
                    </div>
                    <Badge className={`${status.color} border-0`}><StatusIcon className="w-3 h-3 mr-1" />{status.label}</Badge>
                  </div>
                  <div className="bg-surface-highlight rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div><p className="text-xs text-[#666] mb-1">Original</p><p className="font-mono text-[#A1A1AA]">${offer.original_total?.toFixed(2)}</p></div>
                      <div><p className="text-xs text-[#666] mb-1">Their Offer</p><p className="font-mono text-yellow-400 text-lg">${offer.proposed_price?.toFixed(2)}</p><p className="text-xs text-red-400">-{discount}%</p></div>
                      <div><p className="text-xs text-[#666] mb-1">Your Earnings</p><p className="font-mono text-lime">${(offer.proposed_price * 0.9).toFixed(2)}</p></div>
                    </div>
                  </div>
                  <div className="mb-4"><p className="text-xs text-[#666] mb-2">{offer.items?.length} dubplates requested</p></div>
                  {offer.user_message && <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg"><p className="text-xs text-[#666] mb-1">Buyer's message:</p><p className="text-sm text-[#A1A1AA]">{offer.user_message}</p></div>}
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => handleAcceptOffer(offer.id)} disabled={isProcessing} className="btn-primary text-sm" data-testid={`accept-offer-${offer.id}`}>{isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}Accept ${offer.proposed_price?.toFixed(2)}</Button>
                    <Button onClick={() => handleOpenCounterDialog(offer)} disabled={isProcessing} variant="outline" className="text-sm border-purple-500/50 text-purple-400 hover:bg-purple-500/10" data-testid={`counter-offer-${offer.id}`}><HandshakeIcon className="w-4 h-4 mr-2" />Counter Offer</Button>
                    <Button onClick={() => handleDenyOffer(offer.id)} disabled={isProcessing} variant="outline" className="text-sm border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`deny-offer-${offer.id}`}><XCircle className="w-4 h-4 mr-2" />Deny</Button>
                  </div>
                  <p className="text-xs text-[#666] mt-3">Received {new Date(offer.created_at).toLocaleString()}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {historyOffers.length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-bold mb-4">Offer History</h2>
          <div className="space-y-4">
            {historyOffers.map((offer) => {
              const status = offerStatusConfig[offer.status] || offerStatusConfig.pending;
              const StatusIcon = status.icon;
              return (
                <div key={offer.id} className="bg-surface border border-[#262626] rounded-xl p-6" data-testid={`offer-history-${offer.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-surface-highlight rounded-full flex items-center justify-center"><User className="w-5 h-5" /></div>
                      <div><h3 className="font-bold">{offer.user_name}</h3><p className="text-sm text-[#A1A1AA]">{offer.items?.length} dubplates</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      {offer.final_price ? <span className="font-mono text-lime">${offer.final_price.toFixed(2)}</span> : offer.counter_price ? <span className="font-mono text-purple-400">${offer.counter_price.toFixed(2)}</span> : <span className="font-mono text-[#A1A1AA]">${offer.proposed_price?.toFixed(2)}</span>}
                      <Badge className={`${status.color} border-0`}><StatusIcon className="w-3 h-3 mr-1" />{status.label}</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-[#666] mt-3">{new Date(offer.created_at).toLocaleDateString()}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
